import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Plus, Package, Edit, Trash2, Printer, AlertCircle, CheckCircle, AlertTriangle, Minus, PlusCircle, Search } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import Barcode from 'react-barcode';
import { QRCodeCanvas } from 'qrcode.react';
import QRCode from 'qrcode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

function PartsInventory({ user }) {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [quantityUpdateOpen, setQuantityUpdateOpen] = useState(false);
  const [quantityForm, setQuantityForm] = useState({
    quantity: 0,
    change_type: 'add',
    notes: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printCodeType, setPrintCodeType] = useState('barcode'); // 'barcode' or 'qr'
  const [printAllDialogOpen, setPrintAllDialogOpen] = useState(false);
  const [printPartsFilter, setPrintPartsFilter] = useState('all'); // 'all', 'maintenance', 'repair'
  const [formData, setFormData] = useState({
    name: '',
    part_type: 'maintenance',
    code: '',
    current_quantity: 0,
    min_stock_level: 0,
    barcode_type: 'auto',
    barcode_data: '',
    supplier: {
      name: '',
      contact: '',
      phone: '',
      email: ''
    },
    notes: ''
  });

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { is_trial, has_active_subscription, subscription_type } = response.data;
      
      // Allow access if: 
      // 1. User is on free trial OR
      // 2. User has Professional plan subscription (check if subscription name contains "professional")
      const isProfessional = subscription_type?.toLowerCase().includes('professional');
      const allowed = is_trial || (has_active_subscription && isProfessional);
      
      setHasAccess(allowed);
      setCheckingAccess(false);
      
      if (allowed) {
        fetchParts();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Access check failed:', error);
      setCheckingAccess(false);
      setHasAccess(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParts();
  }, []);

  const fetchParts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/parts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParts(response.data);
    } catch (error) {
      console.error('Failed to fetch parts:', error);
      if (error.response?.status === 403) {
        toast({
          variant: 'destructive',
          title: t('professionalPlanRequired'),
          description: t('upgradeToAccessParts')
        });
      } else {
        toast({
          variant: 'destructive',
          title: t('error'),
          description: 'Failed to load parts'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      if (selectedPart) {
        // Update existing part
        await axios.put(`${API}/parts/${selectedPart.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: t('success'),
          description: t('partUpdatedSuccess')
        });
      } else {
        // Create new part
        await axios.post(`${API}/parts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: t('success'),
          description: t('partCreatedSuccess')
        });
      }
      
      setIsFormOpen(false);
      resetForm();
      fetchParts();
    } catch (error) {
      console.error('Failed to save part:', error);
      toast({
        variant: 'destructive',
        title: t('error'),
        description: error.response?.data?.detail || 'Failed to save part'
      });
    }
  };

  const handleDelete = async (partId) => {
    if (!window.confirm(t('confirmDelete'))) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/parts/${partId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: t('success'),
        description: t('partDeletedSuccess')
      });
      
      fetchParts();
    } catch (error) {
      console.error('Failed to delete part:', error);
      toast({
        variant: 'destructive',
        title: t('error'),
        description: 'Failed to delete part'
      });
    }
  };

  const handleQuantityUpdate = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/parts/${selectedPart.id}/quantity`, quantityForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: t('success'),
        description: t('quantityUpdatedSuccess')
      });
      
      setQuantityUpdateOpen(false);
      setQuantityForm({ quantity: 0, change_type: 'add', notes: '' });
      fetchParts();
    } catch (error) {
      console.error('Failed to update quantity:', error);
      toast({
        variant: 'destructive',
        title: t('error'),
        description: error.response?.data?.detail || 'Failed to update quantity'
      });
    }
  };

  const openQuantityUpdate = (part) => {
    setSelectedPart(part);
    setQuantityUpdateOpen(true);
  };

  const openPartDetail = (part) => {
    setSelectedPart(part);
    setIsDetailOpen(true);
  };

  const filteredParts = parts.filter(part => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      part.name.toLowerCase().includes(query) ||
      part.code.toLowerCase().includes(query)
    );
  });

  const executePrintAllParts = () => {
    const printWindow = window.open('', '_blank');
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
    const formattedTime = currentDate.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
    
    // Filter parts based on selection
    let partsTorint = filteredParts;
    if (printPartsFilter === 'maintenance') {
      partsTorint = filteredParts.filter(p => p.part_type === 'maintenance');
    } else if (printPartsFilter === 'repair') {
      partsTorint = filteredParts.filter(p => p.part_type === 'repair');
    } else {
      // For 'all', sort repair parts first, then maintenance
      partsTorint = [
        ...filteredParts.filter(p => p.part_type === 'repair'),
        ...filteredParts.filter(p => p.part_type === 'maintenance')
      ];
    }
    
    const partsTable = partsTorint.map(part => `
      <tr>
        <td style="border: 1px solid #ddd; padding: 8px;">${part.code}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${part.name}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${part.part_type === 'maintenance' ? t('maintenance') : t('repair')}</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${part.current_quantity}</td>
        <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${part.min_stock_level}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${part.supplier?.name || '-'}</td>
      </tr>
    `).join('');
    
    // Title based on filter
    let reportTitle = t('partsInventoryTitle');
    if (printPartsFilter === 'maintenance') {
      reportTitle = `${t('partsInventoryTitle')} - ${t('maintenance')}`;
    } else if (printPartsFilter === 'repair') {
      reportTitle = `${t('partsInventoryTitle')} - ${t('repair')}`;
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Parts Inventory List</title>
          <style>
            @page {
              margin: 0.5in;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            h1 {
              text-align: center;
              margin-bottom: 10px;
            }
            .print-info {
              text-align: center;
              color: #666;
              font-size: 12px;
              margin-bottom: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th {
              background-color: #f0f0f0;
              border: 1px solid #ddd;
              padding: 10px;
              text-align: left;
              font-weight: bold;
            }
            .summary {
              margin-top: 20px;
              padding: 10px;
              background-color: #f9f9f9;
              border: 1px solid #ddd;
            }
          </style>
        </head>
        <body>
          <h1>${reportTitle}</h1>
          <div class="print-info">
            Printed on ${formattedDate} at ${formattedTime}
          </div>
          <div class="summary">
            <strong>Total Parts:</strong> ${partsTorint.length}
          </div>
          <table>
            <thead>
              <tr>
                <th>${t('partCode')}</th>
                <th>${t('partName')}</th>
                <th>${t('partType')}</th>
                <th>${t('quantity')}</th>
                <th>${t('minStockLevel')}</th>
                <th>${t('supplier')}</th>
              </tr>
            </thead>
            <tbody>
              ${partsTable}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              window.print();
              window.close();
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setPrintAllDialogOpen(false);
  };

  const handleEdit = (part) => {
    setSelectedPart(part);
    setFormData({
      name: part.name,
      part_type: part.part_type,
      code: part.code,
      current_quantity: part.current_quantity,
      min_stock_level: part.min_stock_level,
      barcode_type: part.barcode_type,
      barcode_data: part.barcode_data || '',
      supplier: part.supplier || {
        name: '',
        contact: '',
        phone: '',
        email: ''
      },
      notes: part.notes || ''
    });
    setIsFormOpen(true);
  };

  const resetForm = () => {
    setSelectedPart(null);
    setFormData({
      name: '',
      part_type: 'maintenance',
      code: '',
      current_quantity: 0,
      min_stock_level: 0,
      barcode_type: 'auto',
      barcode_data: '',
      supplier: {
        name: '',
        contact: '',
        phone: '',
        email: ''
      },
      notes: ''
    });
  };

  const getStockStatus = (part) => {
    if (part.current_quantity === 0) {
      return { label: t('outOfStock'), color: 'text-red-600', icon: AlertCircle };
    } else if (part.current_quantity <= part.min_stock_level) {
      return { label: t('lowStock'), color: 'text-yellow-600', icon: AlertTriangle };
    } else {
      return { label: t('inStock'), color: 'text-green-600', icon: CheckCircle };
    }
  };

  const handlePrintLabel = (part) => {
    setSelectedPart(part);
    setPrintDialogOpen(true);
  };

  const executePrintLabel = async () => {
    if (!selectedPart) return;
    
    const barcodeValue = selectedPart.barcode_data || selectedPart.code;
    const qrCodeValue = `${selectedPart.name}\n${selectedPart.code}`;
    
    try {
      // Generate QR code as base64 image FIRST (before opening print window)
      let qrCodeDataURL = '';
      if (printCodeType === 'qr') {
        // Use QRCode.toDataURL to generate base64 image
        qrCodeDataURL = await QRCode.toDataURL(qrCodeValue, {
          width: 70,
          margin: 1
        });
        console.log('QR Code generated as data URL');
      }
      
      // Now open print window with pre-generated QR code
      const printWindow = window.open('', '_blank');
      const barcodeValueEscaped = barcodeValue.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/"/g, '\\"');
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Print Label - ${selectedPart.name}</title>
          <style>
            @page {
              size: 2in 1in;
              margin: 0;
            }
            body {
              margin: 0;
              padding: 0;
              width: 2in;
              height: 1in;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .label-container {
              width: 100%;
              height: 100%;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 2px;
              box-sizing: border-box;
            }
            .part-name {
              font-size: 5px;
              font-weight: bold;
              text-align: center;
              margin-bottom: 1px;
              max-width: 100%;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .codes-container {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 3px;
              flex: 1;
            }
          </style>
        </head>
        <body>
          <div class="label-container">
            <div class="part-name">${selectedPart.name}</div>
            <div class="codes-container">
              ${printCodeType === 'barcode' ? '<svg id="barcode"></svg>' : ''}
              ${printCodeType === 'qr' ? `<img id="qrcode" src="${qrCodeDataURL}" style="width: 70px; height: 70px;" />` : ''}
            </div>
          </div>
          
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
          <script>
            function generateAndPrint() {
              try {
                ${printCodeType === 'barcode' ? `
                  // Generate Code 128 barcode
                  JsBarcode("#barcode", "${barcodeValueEscaped}", {
                    format: "CODE128",
                    width: 2,
                    height: 50,
                    displayValue: true,
                    fontSize: 6,
                    margin: 0
                  });
                  console.log('Barcode generated');
                ` : ''}
                
                // QR code is already embedded as image, no generation needed
                console.log('QR Code already loaded');
                
                // Wait a bit for barcode rendering
                setTimeout(function() {
                  window.print();
                  setTimeout(() => window.close(), 500);
                }, 300);
              } catch (error) {
                console.error('Error generating label:', error);
                alert('Error generating label. Please try again.');
                window.close();
              }
            }
            
            // Start generation
            generateAndPrint();
          </script>
        </body>
        </html>
      `);
      
      printWindow.document.close();
      setPrintDialogOpen(false);
    } catch (error) {
      console.error('Error preparing print:', error);
      toast({
        title: "Error",
        description: "Failed to generate print label. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (checkingAccess || loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">{t('loading')}</div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center max-w-md p-8">
          <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('professionalPlanRequired')}</h2>
          <p className="text-gray-600 mb-6">{t('upgradeToAccessParts')}</p>
          <Button 
            onClick={() => window.location.href = '/pricing'}
            className="bg-blue-600 hover:bg-blue-700"
          >
            View Professional Plan
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('partsInventoryTitle')}</h1>
          <p className="text-gray-500 mt-1">{t('partsInventoryDesc')}</p>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setPrintAllDialogOpen(true)}
            className="bg-white hover:bg-gray-50"
          >
            <Printer className="w-4 h-4 mr-2" />
            {t('printAllParts')}
          </Button>
          
          <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                {t('addPart')}
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {selectedPart ? t('editPart') : t('addPart')}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
              {/* Part Name */}
              <div>
                <Label htmlFor="name">{t('partName')} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              
              {/* Part Type */}
              <div>
                <Label htmlFor="part_type">{t('partType')} *</Label>
                <Select value={formData.part_type} onValueChange={(value) => setFormData({ ...formData, part_type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="maintenance">{t('maintenance')}</SelectItem>
                    <SelectItem value="repair">{t('repair')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Part Code */}
              <div>
                <Label htmlFor="code">{t('partCode')}</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="Leave empty for auto-generation"
                  disabled={selectedPart}
                />
                <p className="text-xs text-gray-500 mt-1">Auto-generated if left empty (e.g., MNT-2025-0001)</p>
              </div>
              
              {/* Quantity */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="current_quantity">{t('quantity')}</Label>
                  <Input
                    id="current_quantity"
                    type="number"
                    min="0"
                    value={formData.current_quantity}
                    onChange={(e) => setFormData({ ...formData, current_quantity: parseInt(e.target.value) || 0 })}
                  />
                </div>
                
                <div>
                  <Label htmlFor="min_stock_level">{t('minStockLevel')}</Label>
                  <Input
                    id="min_stock_level"
                    type="number"
                    min="0"
                    value={formData.min_stock_level}
                    onChange={(e) => setFormData({ ...formData, min_stock_level: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              
              {/* Barcode Type */}
              <div>
                <Label htmlFor="barcode_type">{t('barcodeType')}</Label>
                <Select value={formData.barcode_type} onValueChange={(value) => setFormData({ ...formData, barcode_type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{t('autoGenerate')}</SelectItem>
                    <SelectItem value="manual">{t('manual')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Barcode Data (if manual) */}
              {formData.barcode_type === 'manual' && (
                <div>
                  <Label htmlFor="barcode_data">{t('barcode')}</Label>
                  <Input
                    id="barcode_data"
                    value={formData.barcode_data}
                    onChange={(e) => setFormData({ ...formData, barcode_data: e.target.value })}
                  />
                </div>
              )}
              
              {/* Supplier Information */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">{t('supplier')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="supplier_name">{t('supplierName')}</Label>
                    <Input
                      id="supplier_name"
                      value={formData.supplier.name}
                      onChange={(e) => setFormData({
                        ...formData,
                        supplier: { ...formData.supplier, name: e.target.value }
                      })}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="supplier_contact">{t('supplierContact')}</Label>
                    <Input
                      id="supplier_contact"
                      value={formData.supplier.contact}
                      onChange={(e) => setFormData({
                        ...formData,
                        supplier: { ...formData.supplier, contact: e.target.value }
                      })}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="supplier_phone">{t('supplierPhone')}</Label>
                    <Input
                      id="supplier_phone"
                      value={formData.supplier.phone}
                      onChange={(e) => setFormData({
                        ...formData,
                        supplier: { ...formData.supplier, phone: e.target.value }
                      })}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="supplier_email">{t('supplierEmail')}</Label>
                    <Input
                      id="supplier_email"
                      type="email"
                      value={formData.supplier.email}
                      onChange={(e) => setFormData({
                        ...formData,
                        supplier: { ...formData.supplier, email: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <Label htmlFor="notes">{t('notes')}</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {t('savePart')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      </div>
      
      {/* Search Bar */}
      <div className="mb-6">
        <Input
          type="text"
          placeholder={t('searchParts')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-md"
        />
      </div>
      
      {/* Parts Grid */}
      {filteredParts.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchQuery ? 'No parts found' : t('noParts')}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchQuery ? 'Try a different search term' : t('createFirstPart')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredParts.map((part) => {
            const stockStatus = getStockStatus(part);
            const StockIcon = stockStatus.icon;
            
            return (
              <Card 
                key={part.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => openPartDetail(part)}
              >
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1">{part.name}</CardTitle>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {part.code}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          part.part_type === 'maintenance' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {part.part_type === 'maintenance' ? t('maintenance') : t('repair')}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(part);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(part.id);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  {/* Stock Status */}
                  <div className={`flex items-center gap-2 mb-3 ${stockStatus.color}`}>
                    <StockIcon className="w-4 h-4" />
                    <span className="text-sm font-medium">{stockStatus.label}</span>
                  </div>
                  
                  {/* Quantity */}
                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">{t('quantity')}:</span>
                      <span className="font-semibold">{part.current_quantity}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('minStockLevel')}:</span>
                      <span className="font-semibold">{part.min_stock_level}</span>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        openQuantityUpdate(part);
                      }}
                    >
                      <PlusCircle className="w-4 h-4 mr-1" />
                      {t('updateQuantity')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrintLabel(part);
                      }}
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Quantity Update Dialog */}
      <Dialog open={quantityUpdateOpen} onOpenChange={setQuantityUpdateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('updateQuantity')}</DialogTitle>
          </DialogHeader>
          
          {selectedPart && (
            <form onSubmit={handleQuantityUpdate} className="space-y-4">
              <div className="text-sm text-gray-600 mb-4">
                <div className="font-medium">{selectedPart.name}</div>
                <div className="text-xs text-gray-500">{selectedPart.code}</div>
                <div className="mt-2">
                  {t('quantity')}: <span className="font-semibold">{selectedPart.current_quantity}</span>
                </div>
              </div>
              
              <div>
                <Label htmlFor="change_type">{t('partType')}</Label>
                <Select 
                  value={quantityForm.change_type} 
                  onValueChange={(value) => setQuantityForm({ ...quantityForm, change_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="add">{t('addStock')}</SelectItem>
                    <SelectItem value="remove">{t('removeStock')}</SelectItem>
                    <SelectItem value="adjust">{t('adjustStock')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="quantity">{t('quantity')}</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="0"
                  value={quantityForm.quantity}
                  onChange={(e) => setQuantityForm({ ...quantityForm, quantity: parseInt(e.target.value) || 0 })}
                  required
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setQuantityUpdateOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {t('updateQuantity')}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Part Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('partDetails')}</DialogTitle>
          </DialogHeader>
          
          {selectedPart && (
            <div className="space-y-6">
              {/* Part Info */}
              <div>
                <h3 className="text-xl font-bold mb-2">{selectedPart.name}</h3>
                <div className="flex gap-2 mb-4">
                  <span className="text-sm bg-gray-100 text-gray-600 px-2 py-1 rounded">
                    {selectedPart.code}
                  </span>
                  <span className={`text-sm px-2 py-1 rounded ${
                    selectedPart.part_type === 'maintenance' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {selectedPart.part_type === 'maintenance' ? t('maintenance') : t('repair')}
                  </span>
                </div>
              </div>
              
              {/* Barcodes */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">{t('barcodeQRCode')}</h4>
                <div className="flex justify-around items-center">
                  <div className="text-center">
                    <div className="mb-2 text-sm text-gray-600">Code 128</div>
                    <Barcode
                      value={selectedPart.barcode_data || selectedPart.code}
                      width={2}
                      height={80}
                      fontSize={12}
                      displayValue={true}
                    />
                  </div>
                  <div className="text-center">
                    <div className="mb-2 text-sm text-gray-600">QR Code</div>
                    <QRCodeCanvas
                      value={`${selectedPart.name}\n${selectedPart.code}`}
                      size={120}
                    />
                  </div>
                </div>
              </div>
              
              {/* Stock Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="border rounded-lg p-3">
                  <div className="text-sm text-gray-600">{t('quantity')}</div>
                  <div className="text-2xl font-bold">{selectedPart.current_quantity}</div>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="text-sm text-gray-600">{t('minStockLevel')}</div>
                  <div className="text-2xl font-bold">{selectedPart.min_stock_level}</div>
                </div>
              </div>
              
              {/* Supplier Info */}
              {selectedPart.supplier?.name && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">{t('supplier')}</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-gray-600">{t('supplierName')}:</div>
                    <div className="font-medium">{selectedPart.supplier.name}</div>
                    
                    {selectedPart.supplier.contact && (
                      <>
                        <div className="text-gray-600">{t('supplierContact')}:</div>
                        <div className="font-medium">{selectedPart.supplier.contact}</div>
                      </>
                    )}
                    
                    {selectedPart.supplier.phone && (
                      <>
                        <div className="text-gray-600">{t('supplierPhone')}:</div>
                        <div className="font-medium">{selectedPart.supplier.phone}</div>
                      </>
                    )}
                    
                    {selectedPart.supplier.email && (
                      <>
                        <div className="text-gray-600">{t('supplierEmail')}:</div>
                        <div className="font-medium">{selectedPart.supplier.email}</div>
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {/* Notes */}
              {selectedPart.notes && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">{t('notes')}</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedPart.notes}</p>
                </div>
              )}
              
              {/* Quantity History */}
              {selectedPart.quantity_history && selectedPart.quantity_history.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">{t('quantityHistory')}</h4>
                  <div className="space-y-2">
                    {selectedPart.quantity_history.slice().reverse().slice(0, 5).map((history, index) => (
                      <div key={index} className="flex justify-between text-sm border-b pb-2">
                        <div>
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            history.change_type === 'add' ? 'bg-green-100 text-green-700' :
                            history.change_type === 'remove' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {history.change_type}
                          </span>
                          <span className="ml-2 font-medium">{history.quantity}</span>
                          {history.notes && <span className="ml-2 text-gray-500 text-xs">({history.notes})</span>}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {new Date(history.date).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <DialogFooter>
                <Button onClick={() => setIsDetailOpen(false)}>Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Print Label Dialog */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('printLabel')}</DialogTitle>
            <DialogDescription>
              Choose the code type you want to print on the label
            </DialogDescription>
          </DialogHeader>
          
          {selectedPart && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                <div className="font-medium text-base mb-2">{selectedPart.name}</div>
                <div className="text-xs text-gray-500">{selectedPart.code}</div>
              </div>
              
              <div>
                <Label>{t('selectCodeType')}</Label>
                <Select value={printCodeType} onValueChange={setPrintCodeType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="barcode">{t('barcodeOnly')}</SelectItem>
                    <SelectItem value="qr">{t('qrOnly')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
                {t('printSize')}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setPrintDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button onClick={executePrintLabel} className="bg-blue-600 hover:bg-blue-700">
                  <Printer className="w-4 h-4 mr-2" />
                  {t('printLabel')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Print All Parts Filter Dialog */}
      <Dialog open={printAllDialogOpen} onOpenChange={setPrintAllDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('printAllParts')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('selectPartsFilter')}</Label>
              <Select value={printPartsFilter} onValueChange={setPrintPartsFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('allParts')}</SelectItem>
                  <SelectItem value="maintenance">{t('maintenancePartsOnly')}</SelectItem>
                  <SelectItem value="repair">{t('repairPartsOnly')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="text-sm text-gray-600">
              {printPartsFilter === 'all' && (
                <p>{t('allParts')} - Repair parts listed first, then maintenance parts</p>
              )}
              {printPartsFilter === 'maintenance' && (
                <p>Only maintenance parts will be printed</p>
              )}
              {printPartsFilter === 'repair' && (
                <p>Only repair parts will be printed</p>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setPrintAllDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button onClick={executePrintAllParts} className="bg-blue-600 hover:bg-blue-700">
                <Printer className="w-4 h-4 mr-2" />
                {t('printAllParts')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PartsInventory;
